import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Book, Puzzle, Palette, Volume2, Heart, Baby, Leaf, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default images (fallbacks)
const DEFAULT_IMAGES = {
  hero: "https://i.postimg.cc/44p3zQbb/freepik-en-el-mismo-espacio-coloca-a-un-nio-jugando-con-la-31951.png",
  family: "https://images.unsplash.com/photo-1536640712-4d4c36ff0e4e?w=800",
  children: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800"
};

export const FamiliasPage = () => {
  const [content, setContent] = useState({});

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        const pageContent = {};
        response.data.forEach(item => {
          if (item.page === 'familias') {
            pageContent[item.section] = item;
          }
        });
        setContent(pageContent);
      } catch (error) {
      }
    };
    fetchContent();
  }, []);

  // Get content with fallbacks - use 'why' and 'benefits' sections from CMS
  const heroContent = content.hero || {};
  const familyContent = content.why || {};
  const childrenContent = content.benefits || {};

  return (
    <div className="bg-halo-cream" data-testid="familias-page">
      {/* Hero */}
      <section className="relative py-24 bg-halo-charcoal overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={heroContent.image_url || DEFAULT_IMAGES.hero}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-halo-brown font-medium tracking-widest text-sm">
              {heroContent.subtitle || 'SESIONES FAMILIARES'}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-halo-cream mt-4 mb-6">
              {heroContent.title || 'Respirar juntos, desconectar y volver a mirarnos'}
            </h1>
            <p className="text-halo-cream/80 text-lg leading-relaxed">
              {heroContent.text || 'Una invitación a detener el ritmo, apagar la digitalización constante y recuperar algo esencial: el tiempo de calidad entre adultos y niños.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-heading text-4xl text-halo-charcoal mb-6">
                  Un espacio para la <span className="text-halo-brown italic">conexión familiar</span>
                </h2>
                <p className="text-halo-text leading-relaxed text-lg">
                  En HALO, las sesiones familiares no son únicamente un espacio de 
                  haloterapia compartida. Son una invitación a detener el ritmo, 
                  apagar la digitalización constante y recuperar algo esencial: 
                  el tiempo de calidad entre adultos y niños.
                </p>
              </div>

              <p className="text-halo-text leading-relaxed">
                Durante 45 minutos, las familias entran en un entorno diseñado para 
                la calma. Sin pantallas, sin estímulos invasivos, sin prisas. 
                Solo presencia, respiración y conexión.
              </p>

              <div className="bg-halo-surface rounded-2xl p-8 border border-halo-taupe/10">
                <h3 className="font-heading text-xl text-halo-charcoal mb-6">
                  En la sala hemos creado un espacio que incluye:
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: Book, text: 'Biblioteca infantil' },
                    { icon: Puzzle, text: 'Juegos de construcción y encajables' },
                    { icon: Puzzle, text: 'Puzzles' },
                    { icon: Palette, text: 'Zona de arte y creatividad' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-halo-sage/20 rounded-full flex items-center justify-center">
                        <item.icon size={18} className="text-halo-sage" />
                      </div>
                      <span className="text-halo-charcoal text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-halo-text leading-relaxed italic border-l-4 border-halo-brown pl-6">
                "Todo está pensado para favorecer un juego tranquilo, sensorial y creativo, 
                que permita a los niños expresarse mientras respiran los beneficios de la 
                haloterapia en un ambiente seguro."
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <img
                src={familyContent.image_url || DEFAULT_IMAGES.family}
                alt="Familias en HALO"
                className="rounded-2xl w-full h-[350px] object-cover"
              />

              <div className="bg-halo-brown/10 rounded-2xl p-8">
                <h3 className="font-heading text-xl text-halo-charcoal mb-4">
                  Este tiempo compartido beneficia
                </h3>
                <ul className="space-y-3">
                  {[
                    'El sistema respiratorio de toda la familia',
                    'El vínculo entre padres e hijos',
                    'La atención y presencia consciente',
                    'La calidad de la mirada hacia nuestros hijos'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-halo-text">
                      <Heart size={16} className="text-halo-brown flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits for children */}
      <section className="py-24 bg-halo-surface">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-halo-brown font-medium tracking-widest text-sm">
              PARA LOS MÁS PEQUEÑOS
            </span>
            <h2 className="font-heading text-4xl text-halo-charcoal mt-4 mb-4">
              Beneficios de la haloterapia en la infancia
            </h2>
            <p className="text-halo-text max-w-2xl mx-auto">
              La haloterapia actúa como un apoyo natural en el bienestar respiratorio infantil
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Baby,
                title: 'Bronquitis recurrente',
                description: 'Puede actuar como complemento no invasivo que favorece la limpieza bronquial'
              },
              {
                icon: Shield,
                title: 'Procesos respiratorios',
                description: 'Apoyo en procesos respiratorios frecuentes y recuperación tras enfermedades'
              },
              {
                icon: Leaf,
                title: 'Congestión persistente',
                description: 'La acción mucolítica ayuda a fluidificar y expulsar la mucosidad'
              },
              {
                icon: Leaf,
                title: 'Piel atópica',
                description: 'El ambiente salino seco puede aportar beneficios en afecciones cutáneas leves'
              },
              {
                icon: Heart,
                title: 'Regulación emocional',
                description: 'Favorece estados de calma y puede ayudar en procesos de ansiedad leve'
              },
              {
                icon: Volume2,
                title: 'Sobrecarga sensorial',
                description: 'Espacio libre de pantallas y estímulos invasivos para un descanso real'
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-halo-cream p-8 rounded-2xl border border-halo-taupe/10"
              >
                <div className="w-14 h-14 bg-halo-sage/20 rounded-full flex items-center justify-center mb-6">
                  <item.icon size={24} className="text-halo-sage" />
                </div>
                <h3 className="font-heading text-xl text-halo-charcoal mb-3">
                  {item.title}
                </h3>
                <p className="text-halo-text text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Anxiety section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img
                src={childrenContent.image_url || DEFAULT_IMAGES.children}
                alt="Niños en calma"
                className="rounded-2xl w-full h-[400px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6 order-1 lg:order-2"
            >
              <h2 className="font-heading text-4xl text-halo-charcoal">
                {childrenContent.title || 'Haloterapia y ansiedad infantil'}
              </h2>
              <p className="text-halo-text leading-relaxed text-lg">
                {childrenContent.text || 'No debemos olvidar el impacto que la sobreestimulación y la vida acelerada tienen en la infancia actual. Muchos niños viven expuestos a un exceso de pantallas, ruido y actividades continuas que dificultan la regulación emocional.'}
              </p>
              <div className="bg-halo-surface rounded-2xl p-6">
                <p className="text-halo-charcoal font-medium mb-4">La combinación de:</p>
                <ul className="space-y-2">
                  {[
                    'Respiración en ambiente salino',
                    'Espacio sensorialmente equilibrado',
                    'Ausencia de digitalización',
                    'Juego tranquilo y creativo'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-halo-text">
                      <div className="w-2 h-2 bg-halo-brown rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-halo-text mt-4">
                  favorece estados de calma y puede ayudar en procesos de ansiedad 
                  leve o nerviosismo infantil.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 bg-halo-brown">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-heading text-3xl md:text-4xl text-halo-cream italic leading-relaxed">
              "En un mundo cada vez más rápido, ofrecer a los niños un espacio 
              donde respirar, crear y ser mirados con atención es, quizás, 
              uno de los mayores actos de cuidado."
            </p>
            <p className="text-halo-cream/70 mt-8">
              En HALO, las familias no solo respiran mejor.<br />
              <span className="text-halo-cream font-medium">Aprenden a detenerse juntas.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-halo-charcoal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="font-heading text-4xl text-halo-cream">
              Reserva una sesión familiar
            </h2>
            <p className="text-halo-cream/70 max-w-xl mx-auto">
              Regala a tu familia un momento de conexión, calma y bienestar respiratorio.
            </p>
            <Link
              to="/bonos"
              className="inline-flex items-center gap-2 bg-halo-brown text-halo-cream px-8 py-4 rounded-full font-medium hover:bg-halo-charcoal transition-colors"
              data-testid="familias-reservar-btn"
            >
              Comprar bono
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FamiliasPage;
